<template>
  <input
    ref="datePickerInput"
    :id="id"
    :name="name"
    :placeholder="placeholder"
    readonly
    data-type="date"
    type="text"
    autocomplete="off"
  />
</template>

<script>
import holidayJson from '../../common/holidays.json';
import Pikaday from 'pikaday';
import moment from 'moment';
import 'moment/dist/locale/da';

export default {
  name: 'DatePicker',
  props: {
    id: {
      type: String,
      required: true
    },
    value: {
      type: [String, Date, Object],
      default: null
    },
    name: {
      type: String,
      default: ''
    },
    placeholder: {
      type: String,
      default: ''
    },
    date: {
      type: [String, Date, Object],
      default: null
    },
    validate: {
      type: [Object, String],
      default() {
        return {};
      }
    },
    minDate: {
      type: [Date, Object],
      default: null
    },
    maxDate: {
      type: [Date, Object],
      default: null
    },
    setDefaultDate: {
      type: Boolean,
      default: true
    },
    displayFormat: {
      type: String,
      default: window.dateFormat
    },
    saveFormat: {
      type: String,
      default: null
    },
    yearRange: {
      type: Array,
      default() {
        return [1950, moment().year() + 1];
      }
    },
    disableWeekends: {
      type: Boolean,
      default: false
    },
    disableHolidays: {
      type: Boolean,
      default: false
    },
    daysDisabled: {
      type: Array,
      default() {
        return [];
      }
    }
  },
  data() {
    return {
      datePicker: null,
      holidays: holidayJson.holidays
    };
  },
  watch: {
    value(val) {
      this.updateDatePickerDate(val);
    },
    date(val) {
      this.updateDatePickerDate(val);
    },
    minDate() {
      this.updatePickerLimits();
    },
    maxDate() {
      this.updatePickerLimits();
    }
  },
  mounted() {
    this.initializeDatePicker();
  },
  updated() {
    this.initializeDatePicker();
  },
  methods: {
    momentToDate(val) {
      return moment.isMoment(val) ? val.toDate() : val;
    },
    updateDatePickerDate(val) {
      if (val instanceof String) val = Date.parse(val);
      if (this.datePicker && this.datePicker.getDate().toString() !== val.toString()) {
        this.datePicker.setDate(val, true);
      }
    },
    updatePickerLimits() {
      if (!this.datePicker) return;
      
      const minDate = this.momentToDate(this.minDate);
      const maxDate = this.momentToDate(this.maxDate);

      this.datePicker.setMinDate(minDate);
      this.datePicker.setMaxDate(maxDate);
      
      if (moment().toDate() < minDate) {
        this.datePicker.gotoDate(minDate);
      } else if (moment().toDate() > maxDate) {
        this.datePicker.gotoDate(maxDate);
      }
    },
    initializeDatePicker() {
      moment.locale(window.language);

      const date = typeof this.date === 'string' ? new Date(this.date) : this.date;
      const langDa = {
        previousMonth: '<',
        nextMonth: '>',
        months: [
          'januar', 'februar', 'marts', 'april', 'maj', 'juni', 'juli', 'august', 
          'september', 'oktober', 'november', 'december'
        ],
        weekdays: ['søndag', 'mandag', 'tirsdag', 'onsdag', 'torsdag', 'fredag', 'lørdag'],
        weekdaysShort: ['søn', 'man', 'tir', 'ons', 'tor', 'fre', 'lør']
      };

      const holidayArray = this.disableHolidays
        ? this.holidays.filter(holiday => moment(holiday.date).isBefore(this.maxDate)).map(holiday => holiday.date)
        : [];

      if (!this.datePicker) {
        this.datePicker = new Pikaday({
          field: this.$refs.datePickerInput,
          format: this.displayFormat,
          i18n: window.language === 'da' ? langDa : null,
          firstDay: 1,
          minDate: this.momentToDate(this.minDate),
          maxDate: this.momentToDate(this.maxDate),
          defaultDate: this.momentToDate(date),
          setDefaultDate: this.setDefaultDate,
          yearRange: this.yearRange,
          disableWeekends: this.disableWeekends,
          disableDayFn: (date) => {
            const formattedDate = moment(date).format('YYYY-MM-DD');
            return holidayArray.includes(formattedDate) || this.daysDisabled.includes(formattedDate);
          },
          toString: (date, format) => moment(date).format(format),
          onSelect: () => {
            const selectedDate = this.datePicker.getDate();
            const formattedDate = this.saveFormat === null
              ? selectedDate
              : moment(selectedDate).format(this.saveFormat);
            
            this.$emit('date-changed', formattedDate);
            this.$emit('update:modelValue', formattedDate);
          }
        });
      }
    }
  }
};
</script>

<style lang="scss">
@import 'src/styles/all.scss';

.pika-lendar {
  .pika-title {
    .pika-label {
      text-decoration: none;
      color: black;
      font-weight: 500;
      font-size: rem-calc(16px);

      select.pika-select {
        option:disabled {
          color: $black;
          font-weight: 500;
          font-style: italic;
        }
      }
    }
  }

  .pika-table {
    font-size: rem-calc(18px);

    thead {
      background-color: $white;

      abbr {
        color: $black;
        font-weight: 500;
      }
    }

    tr {
      td[aria-selected='true'] {
        button.pika-day {
          font-weight: 500;
          background-color: $black;
          color: $white;
        }
      }
      td[aria-selected='false'] {
        button.pika-day {
          font-weight: 500;
          color: $black;

          &:hover {
            background-color: $black;
            color: $white;
          }
        }

        &.is-disabled {
          button.pika-day {
            color: $black;
            font-weight: 500;
            font-style: italic;
            background-color: $white;
            opacity: .6;
          }
        }
      }

      &:nth-child(even) {
        background-color: $white;
      }
    }
  }
}
</style>
